import type en from '../locales/en.json';

export type LocaleJson = typeof en;
export type LanguageBlock = LocaleJson['language'];

/**
 * All locales in the header language menu.
 * Order: defaults, Europe, Middle East, South/Southeast Asia, East Asia, more Europe.
 */
export const SUPPORTED_UI_LANGUAGES = [
  'en',
  'de',
  'es',
  'fr',
  'pt-BR',
  'it',
  'nl',
  'pl',
  'ru',
  'uk',
  'cs',
  'sk',
  'hr',
  'ro',
  'bg',
  'sl',
  'sv',
  'da',
  'nb',
  'fi',
  'hu',
  'el',
  'tr',
  'ar',
  'he',
  'hi',
  'ms',
  'id',
  'vi',
  'th',
  'ja',
  'ko',
  'zh-CN',
  'zh-TW',
] as const;

export type SupportedUiLanguage = (typeof SUPPORTED_UI_LANGUAGES)[number];

/** Self-names shown in the picker (same in every UI language). */
export const LANGUAGE_ENDONYMS: Record<SupportedUiLanguage, string> = {
  en: 'English',
  de: 'Deutsch',
  es: 'Español',
  fr: 'Français',
  'pt-BR': 'Português (Brasil)',
  it: 'Italiano',
  nl: 'Nederlands',
  pl: 'Polski',
  ru: 'Русский',
  uk: 'Українська',
  cs: 'Čeština',
  sk: 'Slovenčina',
  hr: 'Hrvatski',
  ro: 'Română',
  bg: 'Български',
  sl: 'Slovenščina',
  sv: 'Svenska',
  da: 'Dansk',
  nb: 'Norsk (bokmål)',
  fi: 'Suomi',
  hu: 'Magyar',
  el: 'Ελληνικά',
  tr: 'Türkçe',
  ar: 'العربية',
  he: 'עברית',
  hi: 'हिन्दी',
  ms: 'Bahasa Melayu',
  id: 'Bahasa Indonesia',
  vi: 'Tiếng Việt',
  th: 'ไทย',
  ja: '日本語',
  ko: '한국어',
  'zh-CN': '简体中文',
  'zh-TW': '繁體中文',
};

/** Word for “Language” (menu title) when that locale is active. */
export const LANGUAGE_MENU_BY_UI: Record<SupportedUiLanguage, string> = {
  en: 'Language',
  de: 'Sprache',
  es: 'Idioma',
  fr: 'Langue',
  'pt-BR': 'Idioma',
  it: 'Lingua',
  nl: 'Taal',
  pl: 'Język',
  ru: 'Язык',
  uk: 'Мова',
  cs: 'Jazyk',
  sk: 'Jazyk',
  hr: 'Jezik',
  ro: 'Limbă',
  bg: 'Език',
  sl: 'Jezik',
  sv: 'Språk',
  da: 'Sprog',
  nb: 'Språk',
  fi: 'Kieli',
  hu: 'Nyelv',
  el: 'Γλώσσα',
  tr: 'Dil',
  ar: 'اللغة',
  he: 'שפה',
  hi: 'भाषा',
  ms: 'Bahasa',
  id: 'Bahasa',
  vi: 'Ngôn ngữ',
  th: 'ภาษา',
  ja: '言語',
  ko: '언어',
  'zh-CN': '语言',
  'zh-TW': '語言',
};

export function buildLanguageBlock(ui: SupportedUiLanguage): LanguageBlock {
  const menu = LANGUAGE_MENU_BY_UI[ui];
  const out: Record<string, string> = { menu };
  SUPPORTED_UI_LANGUAGES.forEach((code) => {
    out[code] = LANGUAGE_ENDONYMS[code];
  });
  return out as LanguageBlock;
}
